// ══════════════════════════════════════════════════════════════════════════════
//  src/exercises/CierreDeLeccion.jsx
//  LA CLASE TERMINA DENTRO DE LA CONVERSACIÓN, NO ENCIMA DE ELLA
//
//  ── QUÉ PASABA ─────────────────────────────────────────────────────────────
//  Rob, probando una lección: «terminé mi ejercicio y al finalizar me pedía una
//  respuesta final, pero nunca pude responder porque salió este banner… me
//  bloqueó todo el sistema y mientras lo veía, Spacy seguía hablando».
//
//  El cierre era un cartel modal encima de la pantalla. Dos cosas mal:
//    1. Tapaba y bloqueaba el teclado justo cuando Spacy acababa de hacer una
//       pregunta. La última palabra de la clase se la quedaba un cartel.
//    2. Rompía el personaje. Ningún profesor termina la clase tapándote la
//       cara con un cartel; un profesor lo dice, y tú puedes contestarle.
//
//  ── QUÉ ES ESTO ────────────────────────────────────────────────────────────
//  El cierre como UN MENSAJE MÁS de Spacy, dentro del chat: se queda en el
//  historial, se puede releer, y la caja de escribir nunca deja de funcionar.
//
//  Y sobre todo, sabe dónde está el alumno: no dice «lección completada», dice
//  qué reto ganó, de qué tema, con su nivel y su escenario, y qué le promete el
//  siguiente peldaño EN SUS TÉRMINOS. Los tres formatos se enseñan como un
//  camino —superado / siguiente / te espera—, porque hacerlos los tres es lo
//  que significa dominar el tema.
//
//  El confeti lo sigue lanzando quien cierra la lección; aquí no hay ninguna
//  capa que tape nada.
// ══════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef } from 'react';
import { Check, Zap, ArrowRight, RefreshCw, LogOut, Lock, Trophy } from 'lucide-react';
import { CAMINO, retosDe } from './retosSuperados';
import PideAvisos from '../components/avisos/PideAvisos';
import './CierreDeLeccion.css';

// Las dos Spacys guardan lo mismo con nombres distintos: la lección usa códigos
// de nivel (A1…C2) y modos «spanish_explanations/full_english»; el tutor usa
// «basic/intermediate/advanced» y «assisted/immersion». Al alumno hay que
// decírselo en palabras suyas, no en la etiqueta interna que le tocó.
const NOMBRES_IDIOMA = {
    spanish_explanations: 'con apoyo en español',
    full_english: 'todo en inglés',
    assisted: 'con apoyo en español',
    immersion: 'todo en inglés',
};

const NOMBRES_NIVEL = {
    basic: 'nivel básico',
    intermediate: 'nivel intermedio',
    advanced: 'nivel avanzado',
};

// ── LA MISMA TARJETA PARA LAS DOS SPACYS (24-09-2026) ───────────────────────
//  Rob: «quiero ver que el tutor también lo implementa, así como quedó el de
//  lecciones… para que se vea como de la misma familia».
//
//  Son dos finales distintos y una sola tarjeta:
//    · `leccion` — el alumno GANA un reto de un tema, y el camino de tres
//      peldaños le enseña lo que le falta para dominarlo.
//    · `tutor`   — no hay tema que dominar: es una conversación libre que se
//      termina cuando el alumno quiere. Así que no hay camino, hay lo que hizo
//      y lo que puede hacer ahora.
//
//  Escribir dos tarjetas parecidas era garantizar que dentro de un mes no se
//  parecieran: el día que se toque una, la otra se queda como estaba.
function CierreDeLeccion({ modo = 'leccion', lessonId, tema, stats, config,
                           onSiguiente, onRepetir, onSalir, onCambiarEscenario }) {
    const esTutor = modo === 'tutor';
    // ── QUE SE VEA ENTERA AL APARECER ───────────────────────────────────────
    // La tarjeta es un mensaje más, pero llega sin pasar por `conversation`, y
    // el auto-scroll del chat sólo reacciona a los mensajes. Sin esto, el
    // cierre nace por debajo del borde: el alumno ve confeti y media tarjeta.
    const caja = useRef(null);
    useEffect(() => {
        const t = setTimeout(() => {
            caja.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 120);
        return () => clearTimeout(t);
    }, []);

    const formatoActual = config?.depth_mode || 'fundamentals';
    const ganados = esTutor ? [] : retosDe(lessonId);
    const actual = CAMINO.find(r => r.id === formatoActual);
    const siguiente = esTutor ? null : CAMINO.find(r => ganados.indexOf(r.id) === -1);
    const dominado = !esTutor && !siguiente;

    // Lo que el alumno configuró, escrito como lo diría una persona. Es la
    // frase que hace que el cierre no parezca un formulario: le recuerda que
    // sigue en LO SUYO, sin volver a preguntárselo.
    const suyo = [
        NOMBRES_NIVEL[config?.user_level] || config?.user_level,
        NOMBRES_IDIOMA[config?.language_mode],
        esTutor
            ? (config?.display_info?.user_context && config.display_info.user_context !== 'Libre'
                ? config.display_info.user_context : null)
            : (config?.display_info?.branch === 'business' ? 'tu escenario' : null),
    ].filter(Boolean).join(' · ');

    return (
        <div className="cdl-tarjeta" data-prueba="cierre-leccion" ref={caja}>
            <div className="cdl-cabecera">
                <div className="cdl-sello"><Check size={22} strokeWidth={3} /></div>
                <div className="cdl-titulo-grupo">
                    <div className="cdl-antetitulo">
                        {esTutor ? 'SESIÓN TERMINADA' : (dominado ? 'TEMA DOMINADO' : 'RETO SUPERADO')}
                    </div>
                    <h3 className="cdl-titulo">
                        {esTutor ? 'Buen entrenamiento' : (actual ? actual.nombre : 'Lección')}
                    </h3>
                    {tema && <div className="cdl-tema">{tema}</div>}
                </div>
                <div className="cdl-cifras">
                    <div className="cdl-cifra">
                        <span className="cdl-numero">{stats?.accuracy ?? 100}%</span>
                        <span className="cdl-etiqueta">Precisión</span>
                    </div>
                    <div className="cdl-cifra">
                        <span className="cdl-numero">{stats?.words ?? 0}</span>
                        <span className="cdl-etiqueta">Palabras</span>
                    </div>
                    <div className="cdl-cifra">
                        <span className="cdl-numero">{stats?.phrases ?? 0}</span>
                        <span className="cdl-etiqueta">Frases</span>
                    </div>
                </div>
            </div>

            {esTutor && (
                <p className="cdl-resumen-tutor">
                    Hablaste <strong>{stats?.phrases ?? 0} veces</strong> y soltaste
                    {' '}<strong>{stats?.words ?? 0} palabras</strong> en inglés. Eso es lo que
                    no se consigue leyendo.
                </p>
            )}

            {/* EL CAMINO: dónde está y qué le falta para dominar el tema */}
            {!esTutor && (
            <div className="cdl-camino">
                {CAMINO.map((reto, i) => {
                    const hecho = ganados.indexOf(reto.id) !== -1;
                    const esSiguiente = !!siguiente && reto.id === siguiente.id;
                    return (
                        <div
                            key={reto.id}
                            className={`cdl-peldano ${hecho ? 'hecho' : ''} ${esSiguiente ? 'siguiente' : ''}`}
                        >
                            <div className="cdl-peldano-fila">
                                <span className="cdl-punto">{hecho ? <Check size={13} strokeWidth={3} /> : i + 1}</span>
                                <span className="cdl-peldano-nombre">{reto.nombre}</span>
                                {hecho && <span className="cdl-chapa">Superado</span>}
                                {esSiguiente && <span className="cdl-chapa cdl-chapa-siguiente">Siguiente</span>}
                            </div>
                            <p className="cdl-peldano-nota">
                                {hecho ? 'Ganado en este tema' : (esSiguiente ? reto.promesa : 'Te espera')}
                            </p>
                        </div>
                    );
                })}
            </div>
            )}

            {esTutor && (
                <button type="button" className="cdl-seguir" onClick={onRepetir}
                    data-prueba="cierre-otra-ronda">
                    <Zap size={20} />
                    <span className="cdl-seguir-texto">
                        <strong>Otra ronda, mismo escenario</strong>
                        <em>Sigues donde estabas, con tu nivel y tu apoyo.</em>
                    </span>
                    <ArrowRight size={20} />
                </button>
            )}

            {siguiente && (
                <button type="button" className="cdl-seguir" onClick={() => onSiguiente(siguiente.id)}
                    data-prueba="cierre-siguiente">
                    <Zap size={20} />
                    <span className="cdl-seguir-texto">
                        <strong>Seguir con {siguiente.nombre}</strong>
                        <em>{siguiente.promesa}</em>
                    </span>
                    <ArrowRight size={20} />
                </button>
            )}

            {dominado && (
                <div className="cdl-dominado">
                    <Trophy size={20} />
                    <div>
                        <strong>Dominaste este tema</strong>
                        <p>Lo explicaste, lo hiciste contrarreloj y lo conversaste sin pausas. Eso es dominarlo,
                            no aprobarlo. Si quieres más presión, repite en <strong>Modo Maestro</strong>.</p>
                    </div>
                </div>
            )}

            {suyo && (
                <div className="cdl-suyo">
                    <Lock size={13} />
                    <span>Sigues con lo tuyo: <strong>{suyo}</strong></span>
                </div>
            )}

            {/* ── EL PERMISO DE LOS AVISOS, AQUI Y EN NINGUN OTRO SITIO ──
                Acaba de terminar una leccion: ha sentido que NSL le sirve y
                hay una pausa natural. Es el mejor momento que tiene la app, y
                el momento importa porque un «no» del navegador es DEFINITIVO
                —Chrome no vuelve a preguntar nunca—, asi que pedirlo antes no
                falla: quema la posibilidad para siempre.

                Se ofrece UNA vez. El componente decide solo si toca o no. */}
            <PideAvisos />

            <div className="cdl-secundarios">
                <button type="button" className="cdl-secundario"
                    onClick={esTutor ? onCambiarEscenario : onRepetir}>
                    <RefreshCw size={15} /> {esTutor ? 'Cambiar de escenario' : 'Repetir este reto'}
                </button>
                <button type="button" className="cdl-secundario" onClick={onSalir}>
                    <LogOut size={15} /> Volver al menú
                </button>
            </div>
        </div>
    );
}

export default CierreDeLeccion;
